

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import CropCard from '../components/crops/CropCard';
import BackButton from '../components/common/BackButton';

export default function SearchPage() {
  const navigate  = useNavigate();
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState({ crops: [], farmers: [] });
  const [loading, setLoading] = useState(false);
  const [tab,     setTab]     = useState('crops');
  const inputRef  = useRef();
  const searchRef = useRef('');
  const [allFarmers, setAllFarmers] = useState([]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Search via API with debounce
  useEffect(() => {
  loadFarmers();
}, []);

const loadFarmers = async () => {
  try {
    const res = await api.get('/users/farmers?limit=500');
    setAllFarmers(res.data.farmers || []);
  } catch (err) {
    console.error(err);
  }
};

useEffect(() => {
  searchRef.current = query;

  if (!query.trim()) {
    setResults({ crops: [], farmers: [] });
    return;
  }

  const timer = setTimeout(async () => {
    if (searchRef.current !== query) return;

    setLoading(true);

    try {
      const q = query.trim().toLowerCase();

      // Crop search from API
      const cropRes = await api.get(
        `/crops/search?search=${encodeURIComponent(query)}&limit=50&_=${Date.now()}`
      );

      // Farmer search locally
      const farmers = allFarmers.filter(f =>
        (f.name || '').toLowerCase().includes(q) ||
        (f.locationAddress || '').toLowerCase().includes(q) ||
        (f.mobile || '').includes(q) ||
        (f.city || '').toLowerCase().includes(q) ||
        (f.village || '').toLowerCase().includes(q)
      );

      setResults({
        crops: cropRes.data.crops || [],
        farmers,
      });

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }

  }, 300);

  return () => clearTimeout(timer);

}, [query, allFarmers]);

  return (
    <div className="page">
      {/* ── Search header ── */}
      <div className="header header--simple" style={{ gap:'var(--sp-2)' }}>
        <BackButton />
        <div style={{ flex:1, display:'flex', alignItems:'center', gap:'var(--sp-2)', background:'rgba(255,255,255,.18)', border:'1.5px solid rgba(255,255,255,.4)', borderRadius:'var(--r-full)', padding:'var(--sp-2) var(--sp-4)' }}>
          <span style={{ fontSize:15, color:'white' }}>🔍</span>
          <input
            ref={inputRef}
            style={{ border:'none', outline:'none', flex:1, fontSize:'var(--text-base)', background:'transparent', color:'white', caretColor:'white' }}
            placeholder="Search crops, farmers..."
            value={query}
           onChange={(e) => {
  setQuery(e.target.value);
  setTab(e.target.value ? 'crops' : 'farmers');
}}
          />
          {query && (
            <button onClick={() => { setQuery(''); setTab('crops'); }}
              style={{ background:'rgba(255,255,255,.3)', border:'none', color:'white', fontSize:16, lineHeight:1, width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>×</button>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tabs">
        {[
          { id:'crops',   label:' Crops',   count: results.crops.length },
          { id:'farmers', label:'👨‍🌾 Farmers', count: results.farmers.length },
        ].map(t => (
          <button key={t.id}
            className={`tab${tab === t.id ? ' tab--active' : ''}`}
            onClick={() => setTab(t.id)}>
            {t.label}
            {query && t.count > 0 && <span className="tab__count">{t.count}</span>}
          </button>
        ))}
      </div>

      <div className="container" style={{ paddingTop:'var(--sp-4)' }}>

        {/* Loading */}
        {loading && (
          <div className="full-center" style={{ minHeight:120 }}>
            <span className="spinner spinner--lg" />
          </div>
        )}

        {/* No query */}
        {!loading && !query && (
          <div className="empty-state">
            <div className="empty-state__icon">🔍</div>
            <div className="empty-state__title">Search ZevaZeva</div>
            <div className="empty-state__desc">Find farmers by name or location. Find crops by name or category.</div>
          </div>
        )}

        {/* Crops results */}
        {!loading && query && tab === 'crops' && (
          results.crops.length > 0
            ? results.crops.map(c => <CropCard key={c._id} crop={c} />)
            : <div className="empty-state" style={{ padding:'var(--sp-8) 0' }}>
                <div className="empty-state__icon"></div>
                <div className="empty-state__title">No crops found for "{query}"</div>
                <div className="empty-state__desc">Try a different spelling or category</div>
              </div>
        )}

        {/* Farmers results */}
        {!loading && query && tab === 'farmers' && (
          results.farmers.length > 0
            ? results.farmers.map(f => (
                <div key={f._id} className="card" style={{ marginBottom:'var(--sp-3)', cursor:'pointer' }}
                  onClick={() => navigate(`/farmer/${f._id}`)}>
                  <div className="list-item" style={{ borderBottom:'none' }}>
                    <div className="avatar avatar--md">
                      {f.profilePhoto
                        ? <img src={f.profilePhoto} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' }} />
                        : (f.name?.[0]?.toUpperCase() || 'F')}
                    </div>
                    <div className="list-item__body">
                      <div className="list-item__title">{f.name}</div>
                      <div className="list-item__subtitle"> {f.locationAddress || 'Location not set'}</div>
                      <div className="list-item__subtitle2">
                         {f.cropCount || 0} crops
                        {f.readyCount > 0 && (
                          <span style={{ color:'var(--color-success)', marginLeft:8, fontWeight:600 }}>
                            🟢 {f.readyCount} selling
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="list-item__chevron">›</span>
                  </div>
                </div>
              ))
            : <div className="empty-state" style={{ padding:'var(--sp-8) 0' }}>
                <div className="empty-state__icon">👨‍🌾</div>
                <div className="empty-state__title">No farmers found for "{query}"</div>
                <div className="empty-state__desc">Try a different name or location</div>
              </div>
        )}

      </div>
    </div>
  );
}


